/**
 * User Profile API service
 * Handles user profile updates, password changes, and account operations
 */

import api from './api';
import type {
  UpdateProfileData,
  UpdateProfileResponse,
  ChangePasswordData,
  MessageResponse,
  DisableUserAccountData,
  DisableUserAccountResponse,
  NotificationSettings,
  UpdateNotificationSettingsData,
  UpdateNotificationSettingsResponse,
} from '../types';

export const userProfileService = {
  /**
   * Update user profile (name only)
   * @param data Profile data containing name
   */
  async updateProfile(data: UpdateProfileData): Promise<UpdateProfileResponse> {
    const response = await api.put<UpdateProfileResponse>('/api/user/profile', data);
    return response.data;
  },

  /**
   * Change user password
   * @param data Password change data (current password, new password, confirmation)
   */
  async changePassword(data: ChangePasswordData): Promise<MessageResponse> {
    const response = await api.put<MessageResponse>('/api/user/password', data);
    return response.data;
  },

  /**
   * Disable user account (requires password confirmation)
   * Note: Account owners must transfer ownership before disabling
   * @param data Password for confirmation
   */
  async disableUserAccount(data: DisableUserAccountData): Promise<DisableUserAccountResponse> {
    const response = await api.delete<DisableUserAccountResponse>('/api/user', { data });
    return response.data;
  },

  /**
   * Get the user's Telegram notification settings
   */
  async getNotificationSettings(): Promise<NotificationSettings> {
    const response = await api.get<NotificationSettings>('/api/user/notification-settings');
    return response.data;
  },

  /**
   * Update the user's Telegram notification settings (handle + per-event toggles)
   * @param data Notification settings payload
   */
  async updateNotificationSettings(
    data: UpdateNotificationSettingsData
  ): Promise<UpdateNotificationSettingsResponse> {
    const response = await api.put<UpdateNotificationSettingsResponse>(
      '/api/user/notification-settings',
      data
    );
    return response.data;
  },
};

export default userProfileService;
